// One writer per sheet. Records outlive DOM rows and retain drafts on every failure.
const clone = value => value === undefined ? undefined : JSON.parse(JSON.stringify(value));
const equal = (left, right) => JSON.stringify(left) === JSON.stringify(right);

class TimesheetEditQueue {
    constructor(options) {
        this.options = options;
        this.records = new Map();
        this.queue = [];
        this.running = false;
        this.disposed = false;
        this.epoch = 0;
        this.waiters = [];
        this.holds = 0;
    }

    ensure(key, value, version, meta) {
        if (!this.records.has(key)) {
            this.records.set(key, {key, meta, value: clone(value), baseline: clone(value),
                version, generation: 0, pending: null, inFlight: null, error: null, waiters: []});
        }
        return this.records.get(key);
    }

    dirty(record) { return record.proposal !== undefined || !equal(record.value, record.baseline); }

    update(key, value) {
        const record = this.records.get(key);
        if (!record || equal(record.value, value)) return;
        record.value = clone(value);
        delete record.proposal;
        record.generation += 1;
        this.notify(record);
    }

    notify(record) {
        if (!this.disposed && this.options.onState) this.options.onState(record, {
            dirty: this.dirty(record),
            pending: Boolean(record.pending || record.inFlight),
            error: record.error
        });
    }

    save(key) {
        const record = this.records.get(key);
        if (this.disposed || !record) return Promise.resolve(false);
        if (!this.dirty(record) && !record.inFlight && !record.pending && !record.error) {
            record.error = null;
            this.notify(record);
            return Promise.resolve(true);
        }
        // Conflicts require a deliberate rebase, never an automatic overwrite.
        if (record.error && record.error.conflict) return Promise.resolve(false);
        const promise = new Promise(resolve => record.waiters.push(resolve));
        if ((record.pending && equal(record.pending.value, record.value))
            || (!record.pending && record.inFlight && equal(record.inFlight.value, record.value))) return promise;
        record.pending = {value: clone(record.proposal === undefined ? record.value : record.proposal), generation: record.generation,
            staged: record.proposal !== undefined};
        record.error = null;
        if (!this.queue.includes(key)) this.queue.push(key);
        this.notify(record);
        this.drain();
        return promise;
    }

    async drain() {
        if (this.running || this.disposed || this.holds) return;
        this.running = true;
        while (this.queue.length && !this.disposed && !this.holds) {
            const task = this.queue.shift();
            if (typeof task === 'object') {
                await this.drainBatch(task);
                continue;
            }
            const record = this.records.get(task);
            const edit = record.pending;
            if (!edit) continue;
            record.pending = null;
            record.inFlight = edit;
            this.epoch += 1;
            this.notify(record);
            let success = false;
            try {
                const result = await this.options.send(record, edit.value, record.version);
                if (this.disposed) break;
                record.baseline = clone(result.value);
                record.version = result.version;
                const unchanged = record.generation === edit.generation;
                if (unchanged) { record.value = clone(result.value); delete record.proposal; }
                record.error = null;
                if (this.options.onAccepted) this.options.onAccepted(record, result, unchanged);
                success = true;
            } catch (error) {
                if (this.disposed) break;
                record.error = this.options.describeError(error);
                this.cancelEdit(record, edit);
                // A failed write may have reached the server. Resolve its version before retrying.
                record.pending = null;
            }
            record.inFlight = null;
            this.epoch += 1;
            this.notify(record);
            if (!record.pending) record.waiters.splice(0).forEach(resolve => resolve(success));
        }
        this.running = false;
        this.waiters.splice(0).forEach(resolve => resolve());
        if (!this.disposed && this.options.onIdle) this.options.onIdle();
    }

    cancelEdit(record, edit) {
        if (!record.error || !record.error.cancelled) return;
        const unchanged = record.generation === edit.generation;
        if (unchanged) {
            if (!edit.staged) record.value = clone(record.baseline);
            delete record.proposal;
        }
        record.error = null;
        if (this.options.onCancelled) this.options.onCancelled(record, unchanged);
    }

    async saveBatch(keys, proposal) {
        await this.idle();
        if (this.disposed) return false;
        const records = [...new Set(keys)].map(key => this.records.get(key));
        if (records.some(record => !record || record.error && record.error.conflict)) return false;
        const edits = records.map(record => {
            if (proposal !== undefined) record.proposal = clone(proposal);
            return {record, value: clone(record.proposal === undefined ? record.value : record.proposal),
                staged: record.proposal !== undefined, generation: record.generation};
        });
        edits.forEach(edit => {edit.record.pending = edit; edit.record.error = null; this.notify(edit.record);});
        return new Promise(resolve => {this.queue.push({edits, resolve}); this.drain();});
    }

    async drainBatch(task) {
        const {edits} = task;
        edits.forEach(edit => {edit.record.pending = null; edit.record.inFlight = edit; this.notify(edit.record);});
        this.epoch += 1;
        let success = false;
        try {
            const results = await this.options.sendBatch(edits.map(edit => ({record: edit.record,
                value: edit.value, version: edit.record.version})));
            if (this.disposed) return;
            if (!Array.isArray(results) || results.length !== edits.length) throw new Error('Сервер не подтвердил весь пакет отметок.');
            edits.forEach((edit, index) => {
                const record = edit.record, result = results[index];
                record.baseline = clone(result.value); record.version = result.version;
                const unchanged = record.generation === edit.generation;
                if (unchanged) { record.value = clone(result.value); delete record.proposal; }
                record.error = null;
                if (this.options.onAccepted) this.options.onAccepted(record, result, unchanged);
            });
            success = true;
        } catch (error) {
            if (!this.disposed) edits.forEach(edit => {edit.record.error = this.options.describeError(error);
                this.cancelEdit(edit.record, edit); edit.record.pending = null;});
        } finally {
            edits.forEach(edit => {edit.record.inFlight = null; this.notify(edit.record);});
            this.epoch += 1;
            task.resolve(success);
        }
    }

    merge(key, value, version) {
        const record = this.records.get(key);
        if (!record || record.pending || record.inFlight || this.dirty(record) || record.error) return false;
        record.baseline = clone(value);
        record.value = clone(value);
        record.version = version;
        this.notify(record);
        return true;
    }

    rebase(key, value, version, keepDraft) {
        const record = this.records.get(key);
        if (!record || record.pending || record.inFlight) return false;
        record.baseline = clone(value);
        record.version = version;
        record.error = null;
        if (!keepDraft) { record.value = clone(value); delete record.proposal; record.generation += 1; }
        this.notify(record);
        return true;
    }

    idle() { return this.running ? new Promise(resolve => this.waiters.push(resolve)) : Promise.resolve(); }

    // Finish only the request already in flight. Do not save unrelated drafts.
    async pause() {
        this.holds += 1;
        await this.idle();
        let released = false;
        return () => {
            if (released) return;
            released = true;
            this.holds -= 1;
            this.drain();
        };
    }

    discardLine(lineId) {
        const matches = record => Number(record.meta.lineId) === Number(lineId);
        if ([...this.records.values()].some(record => matches(record) && record.inFlight)) {
            throw new Error('Cannot discard an in-flight row');
        }
        this.queue = this.queue.filter(task => {
            if (typeof task !== 'object') return !matches(this.records.get(task));
            if (!task.edits.some(edit => matches(edit.record))) return true;
            // A queued batch is atomic. Keep other rows as drafts, without sending a partial batch.
            task.edits.forEach(edit => { edit.record.pending = null; this.notify(edit.record); });
            task.resolve(false);
            return false;
        });
        this.records.forEach(record => {
            if (!matches(record)) return;
            record.value = clone(record.baseline);
            record.error = record.pending = null;
            this.notify(record);
            record.waiters.splice(0).forEach(resolve => resolve(false));
            this.records.delete(record.key);
        });
        this.epoch += 1;
    }

    dispose() {
        this.disposed = true;
        this.queue.filter(task => typeof task === 'object').forEach(task => task.resolve(false));
        this.queue = [];
        this.records.forEach(record => record.waiters.splice(0).forEach(resolve => resolve(false)));
        this.waiters.splice(0).forEach(resolve => resolve());
    }
}

module.exports = {TimesheetEditQueue, equal};
